/**
 * Billing and subscribe (TMMAPP-0015) — generated wire types, domain + normalizers.
 * `POST /v1/me/subscription` body is
 * `{ cardToken, idempotencyKey, currency?, timezone?, promoCode?, referralCode?, acceptedTermsVersion,
 * acceptedAutoRenewal }` with the same value on the `Idempotency-Key` header.
 */
import type {
  AppliedPromotion as WireAppliedPromotion,
  BillingProfile as WireBillingProfile,
  BillingReferralView as WireReferral,
  BillingRenewal as WireRenewal,
  BillingRenewalParts as WireRenewalParts,
  BillingTaxLine as WireTaxLine,
  BillingSponsoredSeat as WireSeat,
  ClientAction as WireClientAction,
  PendingChange as WirePendingChange,
  CancelResponse as WireCancel,
  ChangeIntervalRequest,
  PaymentMethodRequest,
  PaymentMethodResponse as WirePaymentMethod,
  PendingSubscribe as WirePendingSubscribe,
  StatementActionView as WireAction,
  MemberStatementLineView as WireLine,
  MemberStatementView as WireStatement,
  SubscribeRequest as WireSubscribeRequest,
  SubscriptionProjection as WireSubscription,
} from './generated';
import { request, requestMeta } from './client';

import { normalizePriceBook, type PriceBook } from './entitlements';
import { normalizeAiWallet, type AiWallet } from './aiWallet';
import { normalizeConsumerTerms, type ConsumerTerms } from './consumerTerms';

/**
 * The subscribe body, as generated: `virtualAccountName` is gone (tmm-api 0153 deprecated it,
 * MM-D-135: a new subscriber starts with no virtual account). Every subscribe carries the consent
 * (MM-D-138, TMMAPI-0156 U1): `acceptedTermsVersion` (the served `consumerTermsVersion` the person
 * accepted) and `acceptedAutoRenewal`. The optional keys are omitted when unset.
 */
export type SubscribeRequest = WireSubscribeRequest;

export type BillingInterval = 'MONTH' | 'YEAR';
export type SubscriptionOrigin = 'SELF' | 'ADMIN' | 'COMP';
export type ChargeStatus =
  'NONE' | 'PENDING' | 'PAID' | 'ACTION_REQUIRED' | 'FAILED' | 'ATTENTION' | 'WAIVED';
export type SeatStatus = 'ACTIVE' | 'ENDING' | 'ENDED';
export type StatementLineKind =
  'RENEWAL' | 'SEAT' | 'AI' | 'PROMOTION' | 'TAX' | 'CONNECTED' | 'OTHER';

export type PromotionKind = 'OFFER' | 'TRIAL_DAYS' | 'PERCENT_OFF_VARIABLE' | 'AMOUNT_OFF_VARIABLE';

const PROMOTION_KINDS: ReadonlySet<string> = new Set([
  'OFFER',
  'TRIAL_DAYS',
  'PERCENT_OFF_VARIABLE',
  'AMOUNT_OFF_VARIABLE',
]);

export interface Subscription {
  id: number;
  status: string;
  origin: SubscriptionOrigin | null;
  interval: BillingInterval | null;
  renewsOn: string | null;
  pendingChange: { interval: BillingInterval; effectiveOn: string } | null;
  retailOfferId: number;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  nextBillingOn: string | null;
  trialEndsOn: string | null;
  compedUntil: string | null;
  maskedCard: string | null;
  cardBrand: string | null;
  payApiSubscriptionId: string | null;
  /**
   * The next renewal's pre-tax amount, as the statutory renewal notice states it (TMMAPI-0160 U3);
   * null for a complimentary, cancelled or ending subscription, and when not served.
   */
  renewal: SubscriptionRenewal | null;
}

/** What the next renewal is made of (TMMAPI-0162 U1). */
export interface SubscriptionRenewalParts {
  /** Charged on `SubscriptionRenewal.on`. */
  baseMinor: number;
  /** Guest seats, billed on statements. */
  seatsMinor: number;
  /** Connected accounts, billed on statements. */
  connectionsMinor: number;
  /** A promotion still applying, as a negative figure; null when none was served. */
  promotionMinor: number | null;
}

export interface SubscriptionRenewal {
  amountMinor: number;
  currency: string;
  interval: BillingInterval;
  /** A civil `YYYY-MM-DD`. */
  on: string;
  parts: SubscriptionRenewalParts;
}

/**
 * The promotion applied to the subscription (TMMAPI-0124). `percentBp` is set for
 * `PERCENT_OFF_VARIABLE`, `amountMinor` + `currency` for `AMOUNT_OFF_VARIABLE`, `retailOfferId`
 * for `OFFER`; each is `null` when absent. `statementsRemaining` `null` = until ended.
 */
export interface AppliedPromotion {
  promotionId: number;
  code: string;
  name: string;
  kind: PromotionKind | null;
  appliedAt: string;
  percentBp: number | null;
  amountMinor: number | null;
  currency: string | null;
  retailOfferId: number | null;
  statementsRemaining: number | null;
}

export interface BillingReferral {
  affiliateDisplayName: string;
  attributedAt: string;
  changeableUntil: string | null;
}

export interface BillingSeat {
  userId: number;
  guestDisplayName: string;
  status: SeatStatus;
  since: string;
  endsAt: string | null;
  monthlyMinor: number;
  currency: string;
}

export interface StatementLine {
  kind: StatementLineKind;
  description: string;
  quantity: number;
  amountMinor: number;
  reference: string | null;
}

/**
 * One jurisdiction of the tax pay-api (Stripe Tax) calculated on a paid statement (TMMAPI-0156 U2,
 * MM-D-139). `ratePercent` is pay-api's own text, shown as served and never computed with.
 */
export interface StatementTaxLine {
  jurisdiction: string | null;
  ratePercent: string | null;
  amountMinor: number;
}

export interface Statement {
  id: number;
  periodStart: string;
  periodEnd: string;
  currency: string;
  renewalMinor: number | null;
  seats: number;
  seatsMinor: number;
  taxMinor: number;
  /** One row per jurisdiction (TMMAPI-0156 U2); empty before the charge and when none was served. */
  taxLines: StatementTaxLine[];
  totalMinor: number;
  chargeStatus: ChargeStatus;
  attempts: number;
  lastAttemptAt: string | null;
  lastError: string | null;
  lines: StatementLine[];
}

export interface PendingSubscribe {
  status: string;
  subscriptionRef: string | null;
  clientAction: ClientAction | null;
  startedAt: string;
}

export interface BillingProfile {
  subscription: Subscription | null;
  prices: PriceBook | null;
  promotion: AppliedPromotion | null;
  referral: BillingReferral | null;
  sponsoredSeats: BillingSeat[];
  statements: Statement[];
  /** The AI wallet's meter (TMMAPI-0151), the same as `GET /v1/me/ai-wallet`; null when absent. */
  aiWallet: AiWallet | null;
  aiSuspendedAt: string | null;
  /** Present while a CREATING subscribe row is parked server-side (TMMAPP-0028). */
  pending: PendingSubscribe | null;
  /**
   * A background charge last failed for want of the card's billing location (TMMAPI-0156 U2,
   * `TAX_LOCATION_REQUIRED`): the person updates the card with its full billing address.
   */
  taxLocationRequired: boolean;
  /** The consumer terms and the seller, as `/bootstrap` serves them (TMMAPI-0156 U1, MM-D-138). */
  consumerTerms: ConsumerTerms;
}

export interface SubscribeResult {
  /** HTTP status from the subscribe response (201 active, 202 CREATING). */
  httpStatus: number;
  /** Body `status` when present on the JsonNode answer (e.g. CREATING). */
  status: string | null;
  clientAction: ClientAction | null;
}

export interface CancelResult {
  status: string;
  endsAt: string;
}

export interface PaymentMethodResult {
  status: string;
  maskedCard: string | null;
  cardBrand: string | null;
}

export interface ClientAction {
  type: string;
  clientSecret: string | null;
  connectedAccountId: string | null;
}

export interface StatementAction {
  status: string;
  clientAction: ClientAction | null;
}

const ORIGINS: ReadonlySet<string> = new Set(['SELF', 'ADMIN', 'COMP']);
const CHARGES: ReadonlySet<string> = new Set([
  'NONE',
  'PENDING',
  'PAID',
  'ACTION_REQUIRED',
  'FAILED',
  'ATTENTION',
  'WAIVED',
]);
const SEAT_STATUSES: ReadonlySet<string> = new Set(['ACTIVE', 'ENDING', 'ENDED']);
/**
 * Every generated statement-line kind. A new wire kind fails here until it has a label.
 * `OTHER` is the domain sentinel for a kind this pin does not name — not a wire value.
 */
const KNOWN_LINE_KINDS = {
  RENEWAL: true,
  SEAT: true,
  AI: true,
  PROMOTION: true,
  TAX: true,
  CONNECTED: true,
} as const satisfies Record<NonNullable<WireLine['kind']>, true>;

const INTERVALS: ReadonlySet<string> = new Set(['MONTH', 'YEAR']);

function normalizeParts(w: WireRenewalParts): SubscriptionRenewalParts {
  return {
    baseMinor: w.baseMinor,
    seatsMinor: w.seatsMinor,
    connectionsMinor: w.connectionsMinor,
    promotionMinor: w.promotionMinor ?? null,
  };
}

/** `subscription.renewal`; null for an interval this build doesn't know. */
function normalizeRenewal(w: WireRenewal): SubscriptionRenewal | null {
  if (!INTERVALS.has(w.interval)) return null;
  return {
    amountMinor: w.amountMinor,
    currency: w.currency,
    interval: w.interval,
    on: w.on,
    parts: normalizeParts(w.parts),
  };
}

function normalizePendingChange(w: WirePendingChange): Subscription['pendingChange'] {
  return INTERVALS.has(w.interval) ? { interval: w.interval, effectiveOn: w.effectiveOn } : null;
}

export function normalizeSubscription(w: WireSubscription): Subscription {
  return {
    id: w.id,
    status: w.status,
    interval: INTERVALS.has(w.interval) ? w.interval : null,
    renewsOn: w.renewsOn ?? null,
    pendingChange: w.pendingChange == null ? null : normalizePendingChange(w.pendingChange),
    origin: ORIGINS.has(w.origin) ? w.origin : null,
    retailOfferId: w.retailOfferId,
    currentPeriodStart: w.currentPeriodStart ?? null,
    currentPeriodEnd: w.currentPeriodEnd ?? null,
    nextBillingOn: w.nextBillingOn ?? null,
    trialEndsOn: w.trialEndsOn ?? null,
    compedUntil: w.compedUntil ?? null,
    maskedCard: w.maskedCard ?? null,
    cardBrand: w.cardBrand ?? null,
    payApiSubscriptionId: w.payApiSubscriptionId ?? null,
    renewal: w.renewal == null ? null : normalizeRenewal(w.renewal),
  };
}

function normalizeSeat(w: WireSeat): BillingSeat {
  return {
    userId: w.userId,
    guestDisplayName: w.guestDisplayName ?? '',
    status: SEAT_STATUSES.has(w.status) ? w.status : 'ACTIVE',
    since: w.since,
    endsAt: w.endsAt ?? null,
    monthlyMinor: w.chargedMinor,
    currency: w.currency,
  };
}

function normalizeLine(w: WireLine): StatementLine {
  // An unknown kind is kept as OTHER so the amounts still sum.
  const kind: StatementLineKind = Object.hasOwn(KNOWN_LINE_KINDS, w.kind) ? w.kind : 'OTHER';
  return {
    kind,
    description: w.description,
    quantity: w.quantity ?? 0,
    amountMinor: w.amountMinor,
    reference: w.reference ?? null,
  };
}

function normalizeTaxLine(w: WireTaxLine): StatementTaxLine {
  return {
    jurisdiction: w.jurisdiction,
    ratePercent: w.ratePercent,
    amountMinor: w.amountMinor,
  };
}

export function normalizeStatement(w: WireStatement): Statement {
  return {
    id: w.id,
    periodStart: w.periodStart,
    periodEnd: w.periodEnd,
    currency: w.currency,
    renewalMinor: w.renewalMinor ?? null,
    seats: w.seats,
    seatsMinor: w.seatsMinor,
    taxMinor: w.taxMinor,
    taxLines: (w.taxLines ?? []).map(normalizeTaxLine),
    totalMinor: w.totalMinor,
    chargeStatus: CHARGES.has(w.chargeStatus) ? w.chargeStatus : 'NONE',
    attempts: w.attempts,
    lastAttemptAt: w.lastAttemptAt ?? null,
    lastError: w.lastError ?? null,
    lines: w.lines.map(normalizeLine),
  };
}

export function normalizeAppliedPromotion(w: WireAppliedPromotion): AppliedPromotion {
  return {
    promotionId: w.promotionId,
    code: w.code,
    name: w.name,
    kind: PROMOTION_KINDS.has(w.kind) ? w.kind : null,
    appliedAt: w.appliedAt,
    percentBp: w.percentBp,
    amountMinor: w.amountMinor,
    currency: w.currency,
    retailOfferId: w.retailOfferId,
    statementsRemaining: w.statementsRemaining,
  };
}

export function normalizeBillingReferral(w: WireReferral): BillingReferral {
  return {
    affiliateDisplayName: w.affiliateDisplayName,
    attributedAt: w.attributedAt,
    changeableUntil: w.changeableUntil ?? null,
  };
}

export function normalizeClientAction(w: WireClientAction): ClientAction {
  return {
    type: w.type ?? '',
    clientSecret: w.clientSecret,
    connectedAccountId: w.connectedAccountId,
  };
}

export function normalizePendingSubscribe(w: WirePendingSubscribe): PendingSubscribe {
  return {
    status: w.status,
    subscriptionRef: w.subscriptionRef ?? null,
    clientAction: w.clientAction == null ? null : normalizeClientAction(w.clientAction),
    startedAt: w.startedAt,
  };
}

/**
 * The subscribe answer is a free `JsonNode` in the contract (201 active, 202 CREATING), so its
 * shape is read here rather than typed: `status` and a `clientAction` object when present.
 */
export function normalizeSubscribeResult(wire: unknown, httpStatus = 201): SubscribeResult {
  const w: { status?: unknown; clientAction?: unknown } =
    typeof wire === 'object' && wire !== null ? wire : {};
  return {
    httpStatus,
    status: typeof w.status === 'string' ? w.status : null,
    clientAction:
      typeof w.clientAction === 'object' && w.clientAction !== null
        ? normalizeClientAction(w.clientAction as WireClientAction)
        : null,
  };
}

export function normalizeBillingProfile(w: WireBillingProfile): BillingProfile {
  return {
    subscription: w.subscription == null ? null : normalizeSubscription(w.subscription),
    prices: w.prices == null ? null : normalizePriceBook(w.prices),
    promotion: w.promotion == null ? null : normalizeAppliedPromotion(w.promotion),
    referral: w.referral == null ? null : normalizeBillingReferral(w.referral),
    sponsoredSeats: w.sponsoredSeats.map(normalizeSeat),
    statements: w.statements.map(normalizeStatement),
    aiWallet: w.aiWallet == null ? null : normalizeAiWallet(w.aiWallet),
    aiSuspendedAt: w.aiSuspendedAt ?? null,
    pending: w.pending == null ? null : normalizePendingSubscribe(w.pending),
    taxLocationRequired: w.taxLocationRequired,
    consumerTerms: normalizeConsumerTerms(w),
  };
}

export function normalizeCancel(w: WireCancel): CancelResult {
  return {
    status: w.status,
    endsAt: w.endsAt,
  };
}

export function normalizePaymentMethod(w: WirePaymentMethod): PaymentMethodResult {
  return {
    status: w.status,
    maskedCard: w.maskedCard ?? null,
    cardBrand: w.cardBrand ?? null,
  };
}

export function normalizeStatementAction(w: WireAction): StatementAction {
  return {
    status: w.status,
    clientAction: w.clientAction == null ? null : normalizeClientAction(w.clientAction),
  };
}

export const billingApi = {
  async changeInterval(interval: BillingInterval): Promise<Subscription> {
    const json: ChangeIntervalRequest = { interval };
    return normalizeSubscription(
      await request<WireSubscription>('POST', '/v1/me/subscription/change-interval', { json }),
    );
  },
  async cancelIntervalChange(): Promise<Subscription> {
    return normalizeSubscription(
      await request<WireSubscription>('DELETE', '/v1/me/subscription/change-interval'),
    );
  },
  async profile(): Promise<BillingProfile> {
    return normalizeBillingProfile(await request<WireBillingProfile>('GET', '/v1/me/billing'));
  },
  /** POST /v1/me/subscription — same idempotency key in body and header. JsonNode answer. */
  async subscribe(body: SubscribeRequest): Promise<SubscribeResult> {
    const meta = await requestMeta<unknown>('POST', '/v1/me/subscription', {
      json: body,
      idempotencyKey: body.idempotencyKey,
    });
    return normalizeSubscribeResult(meta.data, meta.status);
  },
  async cancel(): Promise<CancelResult> {
    return normalizeCancel(await request<WireCancel>('DELETE', '/v1/me/subscription'));
  },
  async paymentMethod(body: PaymentMethodRequest): Promise<PaymentMethodResult> {
    return normalizePaymentMethod(
      await request<WirePaymentMethod>('PUT', '/v1/me/billing/payment-method', {
        json: body,
        idempotencyKey: body.idempotencyKey,
      }),
    );
  },
  async statementAction(statementId: number): Promise<StatementAction> {
    return normalizeStatementAction(
      await request<WireAction>('GET', `/v1/me/billing/statements/${statementId}/action`),
    );
  },
};
